import * as signalR from '@microsoft/signalr'
import useAuth from 'hooks/useAuth/useAuth'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import cookies from 'utilities/cookie'
import getApiRoute from 'utilities/getApiRoute'
import getEnv from 'utilities/getEnv'
import posEvent, { POSEventType } from './events/posHubEvent'
import {
    OperationalSettingContract,
    PrinterContract,
    PaymentResultContract,
    ReceiptBindingDataContract,
    ReceiptTemplateMarkupSlimModel,
    KitchenCategorySortOrder,
    TableGroupContract,
    OrderLineKdsStatus,
} from 'models'
import useReceiptTemplate from 'hooks/useReceiptTemplate/useReceiptTemplate'
import receiptSettingService from 'Services/api/receiptSetting.service'
import { ReceiptTemplateType } from 'constant/receiptTemplateType'
import useNotificationSound from './useNotificationSound'
import {
    UPDATE_KITCHEN_CATEGORY_SORT_ORDER,
    updateOperationalSettingChanged,
} from 'store/actions/operation-setting.action'
import {
    hidePosHubDialog,
    showPosHubDialogError,
    showPosHubDialogWarning,
} from 'store/actions/ui.action'
import { SET_RELOAD_BROWSER_DIALOG_STATUS } from 'store/reducers/ui/reloadDialog.reducer'
import { useAppSelector } from 'store/useStoreHook'
import httpRequest from 'httpRequest'
import { SHOW_FORCE_LOGOUT_DIALOG } from 'store/actions/auth.action'
import { OrderNotificationContract } from 'hooks/useOrderNotification/OrderNotificationContract'
import orderEvent, { OrderEventType } from './events/orderEvent'
import PaymentEvent, {
    PaymentEventType,
} from './events/paymentIntegrationEvent'
import getSampleKitchenReceipt from './sampleKitchenReceipt'
import { deviceStatusSubjects } from 'hooks/useHardwareAgent/hwAgentSubjects'
import {
    DeviceStatusEventType,
    DeviceStatusMessage,
} from 'hooks/useHardwareAgent/Devices/types'
import { SET_CONNECTION_ID } from 'store/reducers/client.reducer'
import eventRouteSettingService from 'Services/eventRouteSetting.service'
import { ActionType } from 'components/EventRouteSettings/types'
import terminalEvent, {
    TerminalEventMessage,
    TerminalEventType,
} from './events/terminalEvent'
import { UPDATE_DEFAULT_PRINTER_SETTING_FROM_BROADCAST_DATA } from 'store/actions/posTerminal.action'
import useCurrentTerminalDevicesHwAgentCaller from 'hooks/useHardwareAgentCaller/useCurrentTerminalDevicesHwAgentCaller'
import { logHWError } from 'utilities/logError'
import { keyBy, values } from 'lodash-es'
import orderLineEvent, { OrderLineEventType } from './events/orderLineEvent'
import tableEvent, { TableEventType } from './events/tableEvent'
import { OrderLineNotificationContract } from 'hooks/useOrderNotification/OrderLineNotificationContract'
import toast from 'utilities/toast/toast'
import authService from 'Services/api/auth.service'
import { defaultSignalRReconnectionDelay } from 'utilities/timeoutHelper'

const sleep = (delayInMilliseconds: number) =>
    new Promise<void>((resolve) => {
        setTimeout(() => {
            resolve()
        }, delayInMilliseconds)
    })

const getTokenForPosHub = async () => {
    try {
        // Ensure that token stored in cookies is valid
        try {
            // Check, and if the token is expired, try refresh-token internally by `httpClient` itself
            await authService.checkToken()

            console.log('POS HUB get token from cookie')
            return cookies.get('token')
        } catch (err: any) {
            // Do nothing because if token error this will cause redirection to login page as normally HTTP Call
        }
    } catch (err) {
        console.log('POS HUB get token from cookie error', err)
    }
}

const newPosHubFn = () => {
    const apiURis = getApiRoute()
    const API_ROOT =
        getEnv('REACT_APP_IS_CLOUD_WEB') === '1'
            ? apiURis.cloud.origin
            : apiURis.local.origin
    console.log('Start Connection to POS HUB')

    return new signalR.HubConnectionBuilder()
        .withUrl(`${API_ROOT}/hubs/posHub`, {
            accessTokenFactory: () => {
                return getTokenForPosHub()
            },
        })
        .configureLogging(signalR.LogLevel.None)
        .withAutomaticReconnect(defaultSignalRReconnectionDelay)
        .build()
}

const POSHubEvent = {
    OnReceiveOrder: 'OnReceiveOrder',
    OnOrderStatusChanged: 'OnOrderStatusChanged',
    OnOrderPaymentStatusChanged: 'OnOrderPaymentStatusChanged',
    OnReserveTable: 'OnReserveTable',
    OnReleaseTable: 'OnReleaseTable',
    OnJoinTables: 'OnJoinTables',
    OnSplitTables: 'OnSplitTables',
    OnReleaseTableGroup: 'OnReleaseTableGroup',
    OnItemAvailabilityChanged: 'OnItemAvailabilityChanged',
    OnOperationalSettingChanged: 'OnOperationalSettingChanged',
    OnPrinterSettingChanged: 'OnPrinterSettingChanged',
    OnPrinterSettingRemoved: 'OnPrinterSettingRemoved',
    OnKitchenSortOrderChanged: 'OnKitchenSortOrderChanged',
    OnPrintKitchenTicketByItems: 'OnPrintKitchenTicketByItems',
    OnTestPrintKitchenTicket: 'OnTestPrintKitchenTicket',
    OnRequestReloadClient: 'OnRequestReloadClient',
    OnRequestForceLogout: 'OnRequestForceLogout',
    OnSettlementPaymentDeviceStatusChanged:
        'OnSettlementPaymentDeviceStatusChanged',
    OnSaleRefundIntegrationStatusChanged:
        'OnSaleRefundIntegrationStatusChanged',
    OnSalePaymentIntegrationStatusChanged:
        'OnSalePaymentIntegrationStatusChanged',
    OnSalePaymentIntegrationStatusCreated:
        'OnSalePaymentIntegrationStatusCreated',
    OnBilledTable: 'OnBilledTable',
    OnTableLayoutChanged: 'OnTableLayoutChanged',
    OnUpdateKDSStatusOrderLine: 'OnUpdateKDSStatusOrderLine',
    OnUpdateTableItemsCount: 'OnUpdateTableItemsCount',
}

const { Disconnected, Connected } = signalR.HubConnectionState

function usePosHub() {
    const dispatch = useDispatch()
    const { isAuthenticated } = useAuth()
    const [playNotification] = useNotificationSound()
    const playNotificationRef = useRef<typeof playNotification>()
    playNotificationRef.current = playNotification
    const isOffline = useAppSelector((state) => state.ui.appState.isOffline)

    /**
     * Use ability of ref for always keep newest version of this function since it contains
     * a lot of dependencies and some de
     * and do not
     * trigger the useEffect that will have a SignalR problem may cause race condition
     */
    const { parsePrintingCommand } = useReceiptTemplate()
    const parsePrintingCommandRef = useRef<typeof parsePrintingCommand>()
    parsePrintingCommandRef.current = parsePrintingCommand
    const { print } = useCurrentTerminalDevicesHwAgentCaller()

    const hubClientRef = useRef<signalR.HubConnection | null>()

    const lastConnectionStatus = useRef<
        undefined | signalR.HubConnectionState
    >()

    const onPosHubConnected = useCallback(() => {
        console.log(
            '[PosHub] connected success with connectionId: ' +
                hubClientRef.current?.connectionId,
        )
        dispatch({
            type: SET_CONNECTION_ID,
            payload: hubClientRef.current?.connectionId || null,
        })
    }, [dispatch])

    const sendDataToPrinter = useCallback(
        async (
            printer: PrinterContract,
            receipt: ReceiptBindingDataContract,
            template: ReceiptTemplateMarkupSlimModel,
            rethrow?: boolean,
            tag?: string,
        ) => {
            const printingCommand = await parsePrintingCommandRef.current!(
                {
                    markup: template?.receiptMarkup || '',
                    data: receipt,
                    receiptTemplateType: template.receiptTemplateTypeID,
                },
                printer,
            )

            try {
                await print(printingCommand, tag, printer)
            } catch (error: any) {
                // Handled the unexception error and logged as information
                logHWError('sendDataToPrinter', 'print', error)
                if (rethrow) throw error
            }
        },
        [print],
    )

    const unbindEventListener = useCallback(() => {
        values(POSHubEvent).forEach((eventName) => {
            hubClientRef.current?.off(eventName)
        })
    }, [])

    const bindEventListener = useCallback(() => {
        if (!hubClientRef.current) return

        unbindEventListener()

        hubClientRef.current.on(POSHubEvent.OnReceiveOrder, (order) => {
            console.log('[PosHub] OnReceiveOrder: ', order)
            if (order)
                orderEvent.next({
                    type: OrderEventType.OnReceiveOrder,
                    params: order,
                })
        })

        hubClientRef.current.on(
            POSHubEvent.OnOrderStatusChanged,
            (order: OrderNotificationContract | null) => {
                console.log('[PosHub] OnOrderStatusChanged: ', order)
                if (order)
                    orderEvent.next({
                        type: OrderEventType.OnOrderStatusChanged,
                        params: order,
                    })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnOrderPaymentStatusChanged,
            (order: OrderNotificationContract | null) => {
                console.log('[PosHub] OnOrderPaymentStatusChanged: ', order)
                if (order)
                    orderEvent.next({
                        type: OrderEventType.OnOrderPaymentStatusChanged,
                        params: order,
                    })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnReserveTable,
            (tableID: string) => {
                console.log('[PosHub] OnReserveTable: ' + tableID)
                posEvent.next({
                    type: POSEventType.OnReserveTable,
                    params: tableID,
                })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnJoinTables,
            (joinedTableIds: string[], tableGroup: TableGroupContract) => {
                const params = { joinedTableIds, tableGroup }
                console.log('[PosHub] OnJoinTables', params)
                posEvent.next({
                    type: POSEventType.OnJoinTables,
                    params,
                })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnSplitTables,
            (ungroupedTableIds: string[], tableGroupId: string) => {
                const params = { ungroupedTableIds, tableGroupId }
                console.log('[PosHub] OnSplitTables', ungroupedTableIds)
                posEvent.next({
                    type: POSEventType.OnSplitTables,
                    params,
                })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnReleaseTable,
            (tableID: string) => {
                console.log('[PosHub] OnReleaseTable: ' + tableID)
                posEvent.next({
                    type: POSEventType.OnReleaseTable,
                    params: tableID,
                })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnReleaseTableGroup,
            (tableGroupId: string) => {
                console.log('[PosHub] OnReleaseTableGroup: ' + tableGroupId)
                posEvent.next({
                    type: POSEventType.OnReleaseTableGroup,
                    params: tableGroupId,
                })
            },
        )
        hubClientRef.current.on(POSHubEvent.OnBilledTable, (data) => {
            console.log('[PosHub] OnBilledTable')
            posEvent.next({
                type: POSEventType.OnBilledTable,
                params: data,
            })
        })

        hubClientRef.current.on(
            POSHubEvent.OnItemAvailabilityChanged,
            (deltaItems) => {
                // Dispatch to sales store or use posEvent to subscribe event
                console.log('[PosHub] OnItemAvailabilityChanged')
                posEvent.next({
                    type: POSEventType.OnItemAvailabilityChanged,
                    params: deltaItems,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnOperationalSettingChanged,
            (operationalSetting: OperationalSettingContract) => {
                console.log('[PosHub] OperationalSettingChanged')
                // Update the change from operation setting to redux store
                // TODO : Session timeout could maintain when the setting change too
                dispatch(updateOperationalSettingChanged(operationalSetting))
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnPrinterSettingChanged,
            (newPrinterSetting: PrinterContract) => {
                console.log(
                    '[PosHub] Printer Setting Changed',
                    newPrinterSetting,
                )
                posEvent.next({
                    type: POSEventType.OnPrinterSettingChanged,
                    params: newPrinterSetting,
                })

                // update cached default printer setting in current app state
                dispatch({
                    type: UPDATE_DEFAULT_PRINTER_SETTING_FROM_BROADCAST_DATA,
                    payload: newPrinterSetting,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnPrinterSettingRemoved,
            (removedPrinterIds: string[]) => {
                console.log(
                    '[PosHub] Printer Setting Removed',
                    removedPrinterIds,
                )

                // Mainly target `DeviceStatusHub` (HW Agent signalR Hub) to send removed printer ids
                posEvent.next({
                    type: POSEventType.OnPrinterSettingRemoved,
                    params: removedPrinterIds,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnKitchenSortOrderChanged,
            (kitchenCategorySortOrders: KitchenCategorySortOrder[]) => {
                console.log(
                    '[PosHub] OnKitchenSortOrderChanged',
                    kitchenCategorySortOrders,
                )
                const payload = keyBy(kitchenCategorySortOrders, 'categoryID')
                dispatch({
                    type: UPDATE_KITCHEN_CATEGORY_SORT_ORDER,
                    payload,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnPrintKitchenTicketByItems,
            async (
                actionReferenceID: string,
                printer: PrinterContract,
                orderLineIds: string[],
            ) => {
                console.log(
                    `[PosHub] OnPrintKitchenTicketByItems: refID = ${actionReferenceID}`,
                    printer,
                    orderLineIds,
                )
                if (orderLineIds.length === 0) {
                    console.log(
                        `OnPrintKitchenTicketByItems@${actionReferenceID}: skip printing because of empty orderLineIds`,
                    )
                    return
                }
                const res = await Promise.all([
                    receiptSettingService.getBillPreviewByItems(orderLineIds),
                    receiptSettingService.getTemplateByType(
                        ReceiptTemplateType.Kitchen,
                    ),
                ])

                try {
                    const previewBills = res[0].data
                    for (const bill of previewBills) {
                        await sendDataToPrinter(
                            printer,
                            // bill preview data
                            bill,
                            // kitchen receipt template
                            res[1].data,
                            // able to rethrow error
                            true,
                            'kp',
                        )
                        // delay 2 seconds per bill
                        await sleep(2000)
                    }

                    // send success ACK (all bills must be successfully printed)
                    eventRouteSettingService.sendEventActionAck(
                        actionReferenceID,
                        ActionType.SendToPrintKitchenTicket,
                        true,
                    )
                } catch (err: any) {
                    // send error ACK
                    let errMessage = `[${err.status}] client with connectionId '${hubClientRef.current?.connectionId}' send kitchen print with error '${err.data.error}'`
                    if (err.data.extras)
                        errMessage +=
                            ' with extras ' + JSON.stringify(err.data.extras)
                    eventRouteSettingService.sendEventActionAck(
                        actionReferenceID,
                        ActionType.SendToPrintKitchenTicket,
                        false,
                        errMessage,
                    )
                }
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnTestPrintKitchenTicket,
            async (printer: PrinterContract) => {
                console.log('[PosHub] OnTestPrintKitchenTicket', printer)

                const kitchenReceiptTemplate = (
                    await receiptSettingService.getTemplateByType(
                        ReceiptTemplateType.Kitchen,
                    )
                ).data
                await sendDataToPrinter(
                    printer,
                    getSampleKitchenReceipt(),
                    kitchenReceiptTemplate,
                    false, // no rethrow
                    'kp-test',
                )
            },
        )

        hubClientRef.current.on(POSHubEvent.OnRequestReloadClient, () => {
            console.log('[PosHub] OnRequestReloadClient')
            dispatch({
                type: SET_RELOAD_BROWSER_DIALOG_STATUS,
                payload: true,
            })
        })

        hubClientRef.current.on(POSHubEvent.OnRequestForceLogout, () => {
            console.log('[PosHub] OnRequestForceLogout')
            dispatch({ type: SHOW_FORCE_LOGOUT_DIALOG })
            httpRequest.deleteAuthorizationCookies()
        })

        hubClientRef.current.on(
            POSHubEvent.OnSettlementPaymentDeviceStatusChanged,
            (deltaItems) => {
                console.log('[PosHub] OnSettlementPaymentDeviceStatusChanged')
                PaymentEvent.next({
                    type:
                        PaymentEventType.OnSettlementPaymentDeviceStatusChanged,
                    params: deltaItems,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnSalePaymentIntegrationStatusChanged,
            (contract: PaymentResultContract) => {
                console.log('[PosHub] OnSalePaymentIntegrationStatusChanged')
                PaymentEvent.next({
                    type:
                        PaymentEventType.OnSalePaymentIntegrationStatusChanged,
                    params: contract,
                })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnSalePaymentIntegrationStatusCreated,
            (contract: PaymentResultContract) => {
                console.log('[PosHub] OnSalePaymentIntegrationStatusCreated')
                PaymentEvent.next({
                    type:
                        PaymentEventType.OnSalePaymentIntegrationStatusCreated,
                    params: contract,
                })
            },
        )
        hubClientRef.current.on(
            POSHubEvent.OnSaleRefundIntegrationStatusChanged,
            (contract: PaymentResultContract) => {
                console.log('[PosHub] OnSaleRefundIntegrationStatusChanged')
                PaymentEvent.next({
                    type: PaymentEventType.OnSaleRefundIntegrationStatusChanged,
                    params: contract,
                })
            },
        )
        hubClientRef.current.on(POSHubEvent.OnTableLayoutChanged, () => {
            console.log('[PosHub] OnTableLayoutChanged')
            posEvent.next({
                type: POSEventType.OnTableLayoutChanged,
            })
        })

        hubClientRef.current.on(
            POSHubEvent.OnUpdateKDSStatusOrderLine,
            (orderLines: OrderLineNotificationContract[]) => {
                console.log('[PosHub] OnUpdateKDSStatusOrderLine: ', orderLines)
                if (
                    orderLines.some(
                        (ol) => ol.kdsStatus === OrderLineKdsStatus.Unknown,
                    )
                ) {
                    toast.error('Unable to sent data to KDS')
                }
                if (orderLines)
                    orderLineEvent.next({
                        type: OrderLineEventType.OnUpdateKDSStatusOrderLine,
                        params: orderLines,
                    })
            },
        )

        hubClientRef.current.on(
            POSHubEvent.OnUpdateTableItemsCount,
            (tables) => {
                console.log('[PosHub] OnUpdateTableItemsCount: ', tables)
                if (tables)
                    tableEvent.next({
                        type: TableEventType.OnUpdateTableItemsCount,
                        params: tables,
                    })
            },
        )
    }, [dispatch, sendDataToPrinter, unbindEventListener])

    useEffect(() => {
        // rebind event listener to ensure that it sync with the current app life cycle state
        // currently, only `print` function need to track the changed of `isOnline` state var
        bindEventListener()

        return () => {
            unbindEventListener()
        }
    }, [bindEventListener, unbindEventListener])

    // Listener for Terminal Events
    useEffect(() => {
        const subscription = terminalEvent.subscribe(
            (message: TerminalEventMessage) => {
                if (
                    !hubClientRef.current ||
                    hubClientRef.current.state !== Connected
                ) {
                    console.log(
                        'drop Terminal Event message due to POSHub is not connected: ' +
                            JSON.stringify(message),
                    )
                    return
                }

                if (message.eventType === TerminalEventType.OnSwitchTerminal) {
                    hubClientRef.current.invoke(
                        'SwitchToNewClient',
                        message.data,
                    )
                }
            },
        )

        return () => {
            subscription.unsubscribe()
        }
    }, [])

    // Listener for DeviceStatus Events
    useEffect(() => {
        const subscription = deviceStatusSubjects.subscribe(
            (message: DeviceStatusMessage) => {
                if (
                    !hubClientRef.current ||
                    hubClientRef.current.state !== Connected
                ) {
                    console.log(
                        'drop Device status message due to POSHub is not connected: ' +
                            JSON.stringify(message),
                    )
                    return
                }

                if (
                    message.eventType ===
                    DeviceStatusEventType.OnPulseBatchStatus
                ) {
                    if (!Array.isArray(message.data)) return

                    const locationID = getEnv(
                        'REACT_APP_LOCATION_TENANT_IDENTIFIER_ID',
                    )

                    // update connectivity table on Cloud API
                    hubClientRef.current
                        .invoke(
                            'UpdateConnectedPrinters',
                            // connected printer ids
                            message.data
                                .filter(
                                    (p) =>
                                        p.isOK && p.locationID === locationID,
                                )
                                .map((p) => p.referenceID),
                            // disconnected printer ids
                            message.data
                                .filter(
                                    (p) =>
                                        !p.isOK && p.locationID === locationID,
                                )
                                .map((p) => p.referenceID),
                        )
                        .then(() => {
                            // broadcast to notify that status update is sent to CloudAPI
                            posEvent.next({
                                type:
                                    POSEventType.OnCompleteSentPrinterStatusToCloudAPI,
                            })
                        })
                } else if (
                    message.eventType ===
                    DeviceStatusEventType.OnSelectiveUpdateStatus
                ) {
                    if (!Array.isArray(message.data)) return

                    const locationID = getEnv(
                        'REACT_APP_LOCATION_TENANT_IDENTIFIER_ID',
                    )

                    // update connectivity table on Cloud API
                    hubClientRef.current
                        .invoke(
                            'UpdateConnectedPrinters',
                            // connected printer ids
                            message.data
                                .filter(
                                    (p) =>
                                        p.isOK && p.locationID === locationID,
                                )
                                .map((p) => p.referenceID),
                            // disconnected printer ids
                            message.data
                                .filter(
                                    (p) =>
                                        !p.isOK && p.locationID === locationID,
                                )
                                .map((p) => p.referenceID),
                        )
                        .then(() => {
                            // broadcast to notify that status update is sent to CloudAPI
                            posEvent.next({
                                type:
                                    POSEventType.OnCompleteSentPrinterStatusToCloudAPI,
                            })
                        })
                } else if (
                    message.eventType ===
                        DeviceStatusEventType.NoPrinterSetting ||
                    message.eventType === DeviceStatusEventType.Disconnected
                ) {
                    hubClientRef.current.invoke('ClearConnectedPrinters')
                }
            },
        )

        return () => {
            subscription.unsubscribe()
        }
    }, [dispatch])

    useEffect(() => {
        /**
         * this effect has to create and init the event
         * Event should be init only once
         * Create the Connection should be be called one time only too
         * Handle connection (stop/start) mostly online with 3 CASE
         * 1. When start app, first Login success, Create a Connection => start
         * 2. When logout just stop connection from (1)
         * 3. When re-login again start the (1) connection
         */

        /**
         * Should called only First enter App and first login
         */
        if (isAuthenticated && !hubClientRef.current) {
            hubClientRef.current = newPosHubFn()
            /**
             * SignalR self state handler, should be init before start
             */
            hubClientRef.current.onclose((error) => {
                console.log('[PosHub] onClose')

                if (error) {
                    console.error('[PosHub] error ', error)
                    if (error.message) {
                        dispatch(
                            showPosHubDialogError(
                                `[PosHub] closed with error: ${error.message}`,
                            ),
                        )
                    }
                }
            })
            hubClientRef.current.onreconnecting((error) => {
                console.log('[PosHub] onreconnecting: ', error)
                dispatch(showPosHubDialogWarning())
            })
            hubClientRef.current.onreconnected((connectionId) => {
                console.log('[PosHub] onreconnected: ', connectionId)
                posEvent.next({ type: POSEventType.OnPOSHubReconnected })
                dispatch({ type: SET_CONNECTION_ID, payload: connectionId })
                dispatch(hidePosHubDialog())
            })

            hubClientRef.current
                .start()
                .then(onPosHubConnected)
                .then(() => {
                    bindEventListener()
                })
                .catch((err) => {
                    console.error('[PosHub] Start POS HUB ERROR (1)', err)
                })
        } else if (!isAuthenticated && hubClientRef && hubClientRef.current) {
            /**
             * When logout, just stop the connection
             */
            hubClientRef.current.stop()
            // There is a chance the reconnecting event was fired before this reached like when the token is expired
            // so we need to cmd to hide the dialog here as well
            dispatch(hidePosHubDialog())
        } else if (isAuthenticated && hubClientRef && hubClientRef.current) {
            if (hubClientRef.current.state === Disconnected) {
                /**
                 * When re-login again
                 */
                hubClientRef.current
                    .start()
                    .then(onPosHubConnected)
                    .then(() => {
                        posEvent.next({
                            type: POSEventType.OnPOSHubReconnected,
                        })
                    })
            }
        }

        return () => {}
    }, [dispatch, isAuthenticated, bindEventListener, onPosHubConnected])

    useEffect(() => {
        const stopWhenOffline = () => {
            if (
                isAuthenticated &&
                hubClientRef.current &&
                hubClientRef.current.state === Connected
            ) {
                hubClientRef.current.stop()
            }
            dispatch(hidePosHubDialog())
        }
        const startWhenOnline = () => {
            if (
                isAuthenticated &&
                hubClientRef.current &&
                hubClientRef.current.state === Disconnected
            ) {
                hubClientRef.current
                    .start()
                    .then(onPosHubConnected)
                    .then(() => {
                        posEvent.next({
                            type: POSEventType.OnPOSHubReconnected,
                        })
                    })
            }
        }
        if (isOffline) stopWhenOffline()
        else startWhenOnline()

        // Manually attempt to reconnect the signalR in case of losing connection due to the absence of
        // the server side while the terminal remains online
        let manualRetryConnection: NodeJS.Timeout | undefined = undefined
        const attemptToStartConnection = () => {
            if (isAuthenticated && hubClientRef.current) {
                const currentState = hubClientRef.current.state

                // manually try to start the connection
                if (currentState === Disconnected) {
                    if (!isOffline)
                        hubClientRef.current
                            .start()
                            .then(onPosHubConnected)
                            .then(() => {
                                posEvent.next({
                                    type: POSEventType.OnPOSHubReconnected,
                                })
                            })
                } else if (
                    currentState === Connected &&
                    lastConnectionStatus.current === Disconnected
                ) {
                    // fall to this mean the connection is connected after stop() -> start() -> connected!
                    // but there's chance that the warning dialog still show, so dispatch action to close it
                    // `lastConnectionStatus` is used to prevent, dispatch closing action multiple times after reconnected
                    dispatch(hidePosHubDialog())
                }

                lastConnectionStatus.current = currentState
            }
            // retry every 10 seconds
            manualRetryConnection = setTimeout(attemptToStartConnection, 10000)
        }
        attemptToStartConnection()

        return () => {
            // clean up the setTimeout when this is unmounted
            if (manualRetryConnection) clearTimeout(manualRetryConnection)
        }
    }, [isAuthenticated, dispatch, isOffline, onPosHubConnected])

    /**
     * clean when this hook was terminated
     */
    useEffect(() => {
        return () => {
            console.log('[PosHub] was terminated')
            if (hubClientRef && hubClientRef.current) {
                hubClientRef.current.stop()
                hubClientRef.current = null
            }
        }
    }, [])
}

export default usePosHub
